import { useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Expand } from "lucide-react";

const PHOTOS = [
  { src: "/images/temple-main.jpg", caption: "Satsang family at Baba's altar", testid: "gallery-image-temple-main" },
  { src: "/images/temple-2.jpg", caption: "The Mandir at dusk, watched over by Hanuman-ji", testid: "gallery-image-temple-2" },
  { src: "/images/hanuman-sunset-2.webp", caption: "Hanuman-ji at golden hour", testid: "gallery-image-hanuman-dusk" },
  { src: "/images/temple-3.jpg", caption: "The youngest devotee's first darshan", testid: "gallery-image-temple-3" },
  { src: "/images/temple-4.jpg", caption: "Marigolds for Maharaj-ji", testid: "gallery-image-temple-4" },
  { src: "/images/temple-5.jpg", caption: "Blessings beside Hanuman-ji", testid: "gallery-image-temple-5" },
  { src: "/images/baba-portrait-2.png", caption: "Maharaj-ji — the blanket-wrapped saint of Neeb Karori", testid: "gallery-image-baba-portrait" },
  { src: "/images/kainchi-dham.jpg", caption: "Kainchi Dham Ashram, Himalayas — photo: Wikimedia Commons (CC BY-SA 4.0)", testid: "gallery-image-kainchi-dham" },
];

export default function PhotoGallery() {
  const [active, setActive] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 sm:py-28 bg-[#EFE8DC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="eyebrow mb-4">06 · Glimpses of the Mandir</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="gallery-heading">
              Moments of Grace in Denison
            </h2>
          </div>
          <p className="text-sm text-stone-500 max-w-sm">
            Photographs from our own temple grounds — tap any image to view it in full.
          </p>
        </div>

        <div className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:balance]">
          {PHOTOS.map((p, i) => (
            <motion.button key={p.src}
              type="button"
              onClick={() => setActive(p)}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              data-testid={p.testid}
              className="relative mb-5 block w-full overflow-hidden rounded-2xl border border-amber-900/10 group cursor-pointer break-inside-avoid"
            >
              <img src={p.src} alt={p.caption} loading="lazy"
                className="w-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-0 inset-x-0 p-4 flex items-center justify-between opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                <span className="text-sm font-medium text-amber-50">{p.caption}</span>
                <Expand className="h-4 w-4 text-amber-200" />
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent data-testid="gallery-lightbox" className="max-w-4xl bg-stone-950 border-stone-800 p-2 sm:p-3">
          {active && (
            <div>
              <img src={active.src} alt={active.caption} className="w-full max-h-[78vh] object-contain rounded-xl" />
              <p className="text-center text-sm text-amber-100/80 py-3 font-quote italic text-base">{active.caption}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
